// Performance V2 — Campaigns tab. Groupings: By Type / By Campaign /
// By Ad Group (#4299) / By Conversion Action (#4301), each with a chart on
// top and a table beneath. Extracted from performance-v2.tsx (#4932 Phase
// 0.3); no behavior change. All row data is computed in the shell (the
// Overview widgets share campaignMetrics/typeMetrics and the conversion-mix
// donut shares the conversion-actions report) and arrives as props, as do
// the #4293 previous-period totals and the #4300 status map.
import { Spinner } from '@/components/glitch-ui/spinner';
import { ColumnDef, CellContext } from '@tanstack/react-table';
import { BarChart3 } from 'lucide-react';
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

import type { CampaignStatusData, ConversionActionRow } from '@/lib/apis';
import type { CampaignData } from '@/lib/types';
import { Card } from '@/components/ui/card';
import { DataTable } from '@/components/glitch-ui/data-table';
import { WidgetGrid } from '@/components/glitch-ui/widget-grid';
import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import type { ExportColumn } from '@/lib/csv-export';
import { cn } from '@/lib/utils';
import { explainImpressionShare } from '../explainers';
import {
  fmt, DeltaCell, StatusPill, CampaignStatePill, ImpressionShareCell, LostIsCell, OptScorePill,
  BidStrategyCell, EmptyChart, ExportCsvButton, CHART_PURPLE,
} from '../cells';

export type CampaignSubView = 'by-type' | 'by-campaign' | 'by-ad-group' | 'by-conversion-action';

export interface TypeMetrics {
  type: string;
  label: string;
  color: string;
  campaigns: number;
  impressions: number;
  clicks: number;
  conversions: number;
  cost: number;
  ctr: number;
  cpc: number;
  cpa: number;
  roas: number;
}

export interface CampaignMetrics {
  id: number;
  /** Google Ads campaign id — used by #4300 status cells to join campaignStatusByGoogleId. */
  googleadsCampaignId: string | null;
  name: string;
  /** Glitch lifecycle state (live/draft/paused/…), rendered as the State column. */
  state: string | null;
  /** True when not Glitch-managed: the report row has no Glitch campaign record, or the
   *  joined record is tagged origin='external' (authoritative — see #6128). */
  isExternal: boolean;
  type: string;
  typeLabel: string;
  typeColor: string;
  impressions: number;
  clicks: number;
  conversions: number;
  cost: number;
  ctr: number;
  cpc: number;
  cpa: number;
  roas: number;
  conversionValue: number;
  adCount: number;
  /** All-conversions + view-through, carried per campaign so totals can apply
   *  the same campaign filters as the primary metrics (#6275). */
  allConversions: number;
  viewThroughConversions: number;
  budget: number;
}

// #4294 / #4299 — one row per ad group with summed metrics (the shell's
// adGroupMetrics rollup memo).
export interface AdGroupMetric {
  adGroupId: string;
  adGroupName: string;
  campaignName: string;
  type: string;
  typeLabel: string;
  typeColor: string;
  impressions: number;
  clicks: number;
  conversions: number;
  cost: number;
  ctr: number;
  cpa: number;
  roas: number;
  adCount: number;
}

// Previous-period campaign totals keyed by Glitch campaign id (#4293).
export interface PreviousCampaignTotals {
  impressions: number;
  clicks: number;
  conversions: number;
  cost: number;
  conversionValue: number;
  ctr: number;
  cpc: number;
  cpa: number;
  roas: number;
}

export function CampaignsTab({
  subView,
  typeMetrics,
  filteredCampaigns,
  adGroupMetrics,
  conversionActions,
  isConversionActionsLoading,
  campaignStatusData,
  campaignStatusByGoogleId,
  previousCampaignTotalsById,
  activeCampaigns,
  visibleCampaignColumns,
  visibleAdGroupColumns,
  currencySymbol,
  dateRangeLabel,
  dateRangeFilter,
  layout,
  onReorder,
  onResize,
}: {
  subView: CampaignSubView;
  typeMetrics: TypeMetrics[];
  filteredCampaigns: CampaignMetrics[];
  adGroupMetrics: AdGroupMetric[];
  conversionActions: ConversionActionRow[] | undefined;
  isConversionActionsLoading: boolean;
  campaignStatusData: CampaignStatusData[] | undefined;
  campaignStatusByGoogleId: Map<string, CampaignStatusData>;
  previousCampaignTotalsById: Map<number, PreviousCampaignTotals>;
  activeCampaigns: CampaignData[];
  visibleCampaignColumns: Set<string>;
  visibleAdGroupColumns: Set<string>;
  currencySymbol: string;
  dateRangeLabel: string;
  dateRangeFilter: { start_date: string; end_date: string };
  layout: WidgetConfig[];
  onReorder: (layout: WidgetConfig[]) => void;
  onResize: (id: string, width: 33 | 50 | 66 | 100) => void;
}) {
  const cs = currencySymbol;

  // Column definitions
  const typeColumns: ColumnDef<TypeMetrics, unknown>[] = [
    { accessorKey: 'label', header: 'Type', cell: ({ row }) => <span className="text-sm font-medium text-gray-900">{row.original.label}</span> },
    { accessorKey: 'campaigns', header: 'Campaigns' },
    { accessorKey: 'impressions', header: 'Imp.', cell: ({ row }) => row.original.impressions.toLocaleString() },
    { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.clicks.toLocaleString() },
    { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => row.original.conversions.toFixed(0) },
    { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => fmt(row.original.cost, cs) },
    { accessorKey: 'cpa', header: 'CPA', cell: ({ row }) => fmt(row.original.cpa, cs) },
    { accessorKey: 'roas', header: 'ROAS', cell: ({ row }) => `${row.original.roas.toFixed(1)}x` },
  ];

  const campaignColumns: ColumnDef<CampaignMetrics, unknown>[] = [
    { accessorKey: 'name', header: 'Campaign', cell: ({ row }) => (
      <div>
        <div className="text-sm font-medium text-gray-900">{row.original.name}</div>
        <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-0.5', row.original.typeColor)}>{row.original.typeLabel}</span>
      </div>
    )},
    // Glitch lifecycle state (Live / Draft / Paused / …) — distinct from the Google *serving* status.
    { id: 'state', header: 'State', cell: ({ row }) => <CampaignStatePill state={row.original.state} /> },
    // #4300 — Google serving-status pill (Limited amber / Eligible green / etc.) with reasons tooltip.
    { id: 'status', header: 'Serving', cell: ({ row }) => {
      const status = row.original.googleadsCampaignId
        ? campaignStatusByGoogleId.get(row.original.googleadsCampaignId)
        : undefined;
      return <StatusPill status={status?.primary_status} reasons={status?.primary_status_reasons ?? []} />;
    }},
    { accessorKey: 'impressions', header: 'Imp.', cell: ({ row }) => (
      <DeltaCell
        current={row.original.impressions}
        previous={previousCampaignTotalsById.get(row.original.id)?.impressions}
        format={(n) => n.toLocaleString()}
      />
    )},
    { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => (
      <DeltaCell
        current={row.original.clicks}
        previous={previousCampaignTotalsById.get(row.original.id)?.clicks}
        format={(n) => n.toLocaleString()}
      />
    )},
    { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => (
      <DeltaCell
        current={row.original.conversions}
        previous={previousCampaignTotalsById.get(row.original.id)?.conversions}
        format={(n) => n.toFixed(0)}
      />
    )},
    { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => (
      <DeltaCell
        current={row.original.cost}
        previous={previousCampaignTotalsById.get(row.original.id)?.cost}
        format={(n) => fmt(n, cs)}
        direction="neutral"
      />
    )},
    { accessorKey: 'cpa', header: 'CPA', cell: ({ row }) => (
      <DeltaCell
        current={row.original.cpa}
        previous={previousCampaignTotalsById.get(row.original.id)?.cpa}
        format={(n) => fmt(n, cs)}
        direction="lower-better"
      />
    )},
    { accessorKey: 'roas', header: 'ROAS', cell: ({ row }) => (
      <DeltaCell
        current={row.original.roas}
        previous={previousCampaignTotalsById.get(row.original.id)?.roas}
        format={(n) => `${n.toFixed(1)}x`}
      />
    )},
    // #4300 — diagnostic columns: Search IS, Lost IS (budget/rank), OptScore, Bid strategy.
    // All join via the Google Ads campaign id; rows for which we have no status row render '—'.
    { id: 'searchIS', header: 'Search IS', cell: ({ row }) => {
      const status = row.original.googleadsCampaignId
        ? campaignStatusByGoogleId.get(row.original.googleadsCampaignId)
        : undefined;
      return <ImpressionShareCell value={status?.search_impression_share ?? null} />;
    }},
    { id: 'lostIS', header: 'Lost IS', cell: ({ row }) => {
      const status = row.original.googleadsCampaignId
        ? campaignStatusByGoogleId.get(row.original.googleadsCampaignId)
        : undefined;
      return (
        <LostIsCell
          budget={status?.search_budget_lost_impression_share ?? null}
          rank={status?.search_rank_lost_impression_share ?? null}
        />
      );
    }},
    { id: 'optScore', header: 'Opt. score', cell: ({ row }) => {
      const status = row.original.googleadsCampaignId
        ? campaignStatusByGoogleId.get(row.original.googleadsCampaignId)
        : undefined;
      return <OptScorePill score={status?.optimization_score ?? null} />;
    }},
    { id: 'bidStrategy', header: 'Bid strategy', cell: ({ row }) => {
      const status = row.original.googleadsCampaignId
        ? campaignStatusByGoogleId.get(row.original.googleadsCampaignId)
        : undefined;
      // Fall back to the strategy on CampaignData (already on the atom) when
      // the live report hasn't loaded yet — saves a flash of em-dash.
      const strategyFromStatus = status?.bidding_strategy_type ?? null;
      const strategyFromAtom = activeCampaigns.find(c => c.id === row.original.id)?.bidding_strategy_type ?? null;
      return <BidStrategyCell strategy={strategyFromStatus ?? strategyFromAtom} />;
    }},
  ];

  // #4299 — Ad-group columns. Used by the Campaigns tab's "By Ad Group"
  // grouping. Lighter than campaignColumns because ad-group-level diagnostics
  // (Status, IS, OptScore) don't exist at this granularity in Google Ads.
  const adGroupColumns: ColumnDef<AdGroupMetric, unknown>[] = [
    { accessorKey: 'adGroupName', header: 'Ad Group', cell: ({ row }) => (
      <div>
        <div className="text-sm font-medium text-gray-900">{row.original.adGroupName}</div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-xs text-gray-500">{row.original.campaignName}</span>
          <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium', row.original.typeColor)}>{row.original.typeLabel}</span>
        </div>
      </div>
    )},
    { accessorKey: 'adCount', header: 'Ads', cell: ({ row }) => row.original.adCount.toLocaleString() },
    { accessorKey: 'impressions', header: 'Imp.', cell: ({ row }) => row.original.impressions.toLocaleString() },
    { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.clicks.toLocaleString() },
    { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => row.original.conversions.toFixed(0) },
    { accessorKey: 'ctr', header: 'CTR', cell: ({ row }) => `${row.original.ctr.toFixed(2)}%` },
    { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => fmt(row.original.cost, cs) },
    { accessorKey: 'cpa', header: 'CPA', cell: ({ row }) => fmt(row.original.cpa, cs) },
    { accessorKey: 'roas', header: 'ROAS', cell: ({ row }) => `${row.original.roas.toFixed(1)}x` },
  ];

  return (
    <WidgetGrid layout={layout} onReorder={onReorder} onResize={onResize}>
      {(widgetId, _widgetWidth) => {
        switch (widgetId) {
          case 'campaigns-chart':
            if (subView === 'by-type') return (
              <Card className="p-4 border border-gray-200 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">CPA by Campaign Type</h4>
                {typeMetrics.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={typeMetrics} barGap={4}>
                      <CartesianGrid vertical={false} stroke="#f3f4f6" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                      <Tooltip formatter={(value) => `${cs}${Number(value).toFixed(2)}`} contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 12 }} />
                      <Bar dataKey="cpa" name={`CPA (${cs})`} radius={[4, 4, 0, 0]}>
                        {typeMetrics.map((_, i) => <Cell key={i} fill={CHART_PURPLE[i % CHART_PURPLE.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart icon={BarChart3} message="Campaign type comparison will appear once campaigns have performance data" />
                )}
              </Card>
            );
            if (subView === 'by-campaign') return (
              <Card className="p-4 border border-gray-200 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">Cost by Campaign</h4>
                {filteredCampaigns.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(filteredCampaigns.length * 44, 180)}>
                    <BarChart data={filteredCampaigns} layout="vertical" barSize={18}>
                      <CartesianGrid horizontal={false} stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tick={{ fontSize: 10, fill: '#6b7280' }}
                        width={160}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(name: string) => name.length > 22 ? name.slice(0, 22) + '...' : name}
                      />
                      <Tooltip
                        formatter={(value) => `${cs}${Number(value).toFixed(2)}`}
                        contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 12 }}
                      />
                      <Bar dataKey="cost" fill="#290BD9" name={`Cost (${cs})`} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart icon={BarChart3} message="Campaign cost comparison will appear once campaigns have spend data" />
                )}
              </Card>
            );
            if (subView === 'by-ad-group') {
              // #4299 — Cost by Ad Group (top 15 by spend). Same horizontal
              // bar layout as the by-campaign chart for visual consistency.
              const topAdGroups = adGroupMetrics.slice().sort((a, b) => b.cost - a.cost).slice(0, 15);
              return (
                <Card className="p-4 border border-gray-200 shadow-sm">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">Cost by Ad Group (top 15)</h4>
                  {topAdGroups.length > 0 ? (
                    <ResponsiveContainer width="100%" height={Math.max(topAdGroups.length * 32, 180)}>
                      <BarChart data={topAdGroups} layout="vertical" barSize={16}>
                        <CartesianGrid horizontal={false} stroke="#f3f4f6" />
                        <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                        <YAxis
                          type="category"
                          dataKey="adGroupName"
                          tick={{ fontSize: 10, fill: '#6b7280' }}
                          width={180}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(name: string) => name.length > 26 ? name.slice(0, 26) + '...' : name}
                        />
                        <Tooltip
                          formatter={(value) => `${cs}${Number(value).toFixed(2)}`}
                          contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 12 }}
                        />
                        <Bar dataKey="cost" fill="#290BD9" name={`Cost (${cs})`} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <EmptyChart icon={BarChart3} message="Ad-group cost comparison will appear once ad groups have spend data" />
                  )}
                </Card>
              );
            }
            return <div />;

          case 'campaigns-table':
            if (subView === 'by-type') return <DataTable<TypeMetrics> data={typeMetrics} columns={typeColumns} pageSize={10} variant="bordered" emptyState="No performance data available for this period" />;
            if (subView === 'by-campaign') {
              // #4294 — only the columns the user has chosen to see.
              // External campaigns aren't Glitch-managed, so every performance column reads "NA"
              // rather than Google numbers Glitch didn't drive (the External type badge marks why).
              // Allowlist the identity/config columns to KEEP (name, state, serving status, bid
              // strategy); everything else is blanked — robust as new metric columns are added.
              const KEEP_FOR_EXTERNAL = new Set(['name', 'state', 'status', 'bidStrategy']);
              const visibleColumns = campaignColumns
                .filter(c => {
                  const id = c.id ?? ((c as { accessorKey?: string }).accessorKey);
                  return id ? visibleCampaignColumns.has(id) : true;
                })
                .map(c => {
                  const id = c.id ?? ((c as { accessorKey?: string }).accessorKey);
                  if (!id || KEEP_FOR_EXTERNAL.has(id) || typeof c.cell !== 'function') return c;
                  const orig = c.cell;
                  return {
                    ...c,
                    cell: (ctx: CellContext<CampaignMetrics, unknown>) =>
                      ctx.row.original.isExternal ? <span className="text-xs text-gray-400">NA</span> : orig(ctx),
                  };
                });
              // #4306 — auto-explainer footnote on Search IS status.
              const isNarrative = explainImpressionShare(campaignStatusData ?? []);
              // #4302 — CSV export. Mirrors the Google Ads UI Campaign report column set.
              const exportColumns: ExportColumn<CampaignMetrics>[] = [
                { header: 'Campaign', value: r => r.name },
                { header: 'Type', value: r => r.typeLabel },
                { header: 'Impressions', value: r => r.impressions },
                { header: 'Clicks', value: r => r.clicks },
                { header: 'CTR', value: r => `${r.ctr.toFixed(2)}%` },
                { header: 'Conversions', value: r => r.conversions },
                { header: 'Cost', value: r => r.cost },
                { header: 'CPA', value: r => r.cpa },
                { header: 'ROAS', value: r => `${r.roas.toFixed(2)}x` },
              ];
              return (
                <div className="space-y-2">
                  <div className="flex items-center justify-end pt-1 pr-10">
                    <ExportCsvButton
                      rows={filteredCampaigns}
                      columns={exportColumns}
                      title="Campaign report"
                      dateRange={dateRangeLabel}
                      startDate={dateRangeFilter.start_date}
                      endDate={dateRangeFilter.end_date}
                      filenameSlug="campaign-report"
                    />
                  </div>
                  <DataTable<CampaignMetrics> data={filteredCampaigns} columns={visibleColumns} pageSize={10} variant="bordered" emptyState="No campaign data available for this period" />
                  {isNarrative && <p className="text-xs text-gray-400 px-1">{isNarrative}</p>}
                </div>
              );
            }
            if (subView === 'by-ad-group') {
              // #4299 — Ad-group rollup. Per the plan, this lives as a
              // grouping option on the Campaigns table rather than as a
              // standalone sub-tab in a future iteration with the column
              // picker extended to ad-group columns.
              // #4310 — Filter ad-group columns by user-chosen visibility.
              const visibleAdGroupColumnDefs = adGroupColumns.filter(c => {
                const id = c.id ?? ((c as { accessorKey?: string }).accessorKey);
                return id ? visibleAdGroupColumns.has(id) : true;
              });
              return <DataTable<AdGroupMetric> data={adGroupMetrics} columns={visibleAdGroupColumnDefs} pageSize={15} variant="bordered" emptyState="No ad-group performance data available for this period" />;
            }
            if (subView === 'by-conversion-action') {
              // #4301 — per-campaign × conversion-action breakdown.
              // One row per (campaign, conversion action). Cost is
              // surfaced at the campaign level because Google Ads
              // doesn't expose per-action spend.
              const conversionActionColumns: ColumnDef<ConversionActionRow, unknown>[] = [
                { accessorKey: 'conversion_action_name', header: 'Conversion action', cell: ({ row }) => (
                  <div>
                    <div className="text-sm font-medium text-gray-900">{row.original.conversion_action_name ?? 'Unknown'}</div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                      {row.original.conversion_action_category && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                          {row.original.conversion_action_category.toLowerCase().replace(/_/g, ' ')}
                        </span>
                      )}
                      <span className="truncate max-w-[200px]">{row.original.campaign_name}</span>
                    </div>
                  </div>
                )},
                { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => row.original.conversions.toFixed(0) },
                { accessorKey: 'all_conversions', header: 'All conv.', cell: ({ row }) => row.original.all_conversions.toFixed(0) },
                { accessorKey: 'all_conversions_value', header: 'Conv. value', cell: ({ row }) => fmt(row.original.all_conversions_value, cs) },
                { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => fmt(row.original.cost, cs) },
                { accessorKey: 'cost_per_conv', header: 'Cost/conv', cell: ({ row }) => row.original.conversions > 0 ? fmt(row.original.cost_per_conv, cs) : <span className="text-xs text-gray-400">—</span> },
              ];
              const hasConvActions = Array.isArray(conversionActions) && conversionActions.length > 0;
              if (isConversionActionsLoading && !hasConvActions) {
                return (
                  <div className="flex items-center justify-center py-16">
                    <Spinner size="lg" className="text-gray-400" />
                    <span className="ml-3 text-sm text-gray-500">Loading conversion-action breakdown…</span>
                  </div>
                );
              }
              return (
                <DataTable<ConversionActionRow>
                  data={conversionActions ?? []}
                  columns={conversionActionColumns}
                  pageSize={20}
                  variant="bordered"
                  emptyState="No conversion-action data available for this period"
                />
              );
            }
            return <div />;

          default:
            return null;
        }
      }}
    </WidgetGrid>
  );
}
